import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useIsEditMode } from '@/hooks/useIsEditMode'
import { useRouter } from 'next/router'

import { Box } from '@chakra-ui/react'

import { FLOOR_PLAN } from '@/utils/routes'

const withoutTrailingSlash = (path: string) => path.replace(/\/+$/, '')

const isFloorPlanRoute = (pathname: string) =>
  withoutTrailingSlash(pathname) === withoutTrailingSlash(FLOOR_PLAN)

/**
 * The mint border drawn around the page while Edit Mode is on.
 *
 * Which pages draw it depends on the `removeEditMode` feature flag:
 *
 * - **flag off** — the old world. The Side Bar toggle governs the whole app, so
 *   every page that mounts this renders the frame.
 * - **flag on** — Edit Mode survives only on the Floor Plan, where its toggle
 *   now lives, because moving Devices is the one place in the product where we
 *   can accidentally mutate things. Everywhere else this renders nothing.
 *
 * Call sites therefore don't need to know about the flag — mount it and it
 * works out whether it belongs.
 *
 * @deprecated Edit Mode is being retired behind the `removeEditMode` feature
 * flag. Do _NOT_ add new call sites — this component is deleted along with the
 * rest of Edit Mode once that flag is fully rolled out. See
 * `useIsEditModeSatisfied` in `@/hooks/useIsEditModeSatisfied` for how gated
 * actions are expected to check Edit Mode in the meantime.
 */
export const EditModeFrame = () => {
  const isRemoveEditModeEnabled = useFeatureFlag(FeatureFlag.removeEditMode)
  const { pathname } = useRouter()
  // Keep as old Edit Mode check!
  const isEditMode = useIsEditMode()

  if (isRemoveEditModeEnabled && !isFloorPlanRoute(pathname)) return null

  return (
    <Box
      borderColor={isEditMode ? '#46ef9f' : 'transparent'}
      borderWidth={isEditMode ? '5px' : '0px'}
      data-testid={`EditModeFrame:${isEditMode ? 'active' : 'inactive'}`}
      h='full'
      left='0'
      pointerEvents='none'
      pos='absolute'
      top='0'
      transition='ease-in-out'
      transitionDuration='300ms'
      transitionProperty='all'
      w='full'
      zIndex='9'
    />
  )
}
